<template>
  <div class="card mb-4">

    <!-- UPDATED: Responsive flex classes to prevent overlap in header -->
    <div class="card-header pb-0 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
      <h6 class="mb-0">{{ $t('media') }}</h6>
      
      <!-- UPDATED: Flex-wrap and justify content for mobile buttons -->
      <div class="d-flex flex-wrap align-items-center w-100 w-md-auto justify-content-between justify-content-md-end gap-2">
        <!-- VIEW TOGGLE BUTTONS -->
        <div class="btn-group btn-group-sm shadow-sm">
          <button 
            class="btn btn-sm mb-0" 
            :class="viewMode === 'table' ? 'btn-primary text-white' : 'btn-outline-primary'" 
            @click="viewMode = 'table'"
          >
            <i class="fas fa-table"></i>
          </button>
          <button 
            class="btn btn-sm mb-0" 
            :class="viewMode === 'card' ? 'btn-primary text-white' : 'btn-outline-theme'" 
            @click="viewMode = 'card'"
          >
            <i class="fas fa-th-large"></i>
          </button>
        </div>

        <!-- Updated: Dynamic style for Add Button and added responsive growth -->
        <button type="button" class="btn btn-primary mb-0 flex-grow-1 flex-md-grow-0" @click="showModal('add')"
          :style="{ backgroundColor: 'var(--bs-primary)', color: 'white', border: '2px solid var(--bs-primary)' }">
          {{ $t('media_add') }}
        </button>
      </div>
    </div>
    
    <div class="card-body px-0 pt-0 pb-2">
      
      <!-- 1. TABLE VIEW -->
      <div v-if="viewMode === 'table'" class="table-responsive p-0">
        <table class="table align-items-center mb-0">
          <thead>
            <tr>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-left">{{
                $t('media_type') }}</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-left">{{
                $t('media_name') }}</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-left">{{
                $t('status') }}</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-left">{{
                $t('action') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(role, index) in paginatedRoles" :key="index">

              <td class="text-left">
                <p class="text-xs font-weight-bold mb-0">{{ role.type == 1 ? 'Image' : 'Video' }}</p>
              </td>
              <td class="text-left">
               <p class="text-xs font-weight-bold mb-0 text-dark">{{ role.name }}</p>
              </td>
              <td class="text-left">
                <soft-badge :color="role.status == 1 ? 'success' : 'secondary'" variant="gradient" size="sm">{{
                  role.status == 1 ? 'Active' : "InActive" }}</soft-badge>
              </td>
              <td class="text-left">
                <a href="javascript:;" class="text-secondary font-weight-bold text-xs"
                  @click="showModal('edit', (currentPage - 1) * itemsPerPage + index)">
                  <i class="fas fa-edit"></i>
                </a>
                <a href="javascript:;"
                  class="text-danger font-weight-bold text-xs ms-3" @click="confirmDelete((currentPage - 1) * itemsPerPage + index, role.id)">
                  <i class="fas fa-trash-alt"></i>
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 2. CARD VIEW -->
      <div v-else class="px-3 px-md-4 pb-4">
        <div class="row">
          <!-- UPDATED: Added col-12 for full width on mobile devices -->
          <div v-for="(role, index) in paginatedRoles" :key="'card-' + index" class="col-12 col-md-6 col-xl-3 mb-4">
            <div class="card h-100 border shadow-sm overflow-hidden">
              <!-- Media Preview Area -->
              <div class="position-relative bg-dark d-flex align-items-center justify-content-center" style="height: 160px;">
                <img v-if="role.type == 1" :src="role.media_url || resourcePhotoUrl" class="w-100 h-100 object-fit-cover" />
                <video v-else :src="role.media_url" class="w-100 h-100 object-fit-cover"></video>
                
                <div class="position-absolute top-0 end-0 m-2">
                  <soft-badge :color="role.status == 1 ? 'success' : 'secondary'" variant="gradient" size="sm">
                    {{ role.status == 1 ? 'Active' : "InActive" }}
                  </soft-badge>
                </div>
                
                <div v-if="role.type == 0" class="position-absolute top-50 start-50 translate-middle">
                   <i class="fas fa-play-circle text-white opacity-8 fa-2x"></i>
                </div>
              </div>

              <div class="card-body p-3">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <div class="overflow-hidden">
                    <h6 class="mb-0 text-sm text-truncate text-dark" :title="role.name">{{ role.name }}</h6>
                    <p class="text-xxs text-secondary mb-0 uppercase">{{ role.type == 1 ? 'Image' : 'Video' }}</p>
                  </div>
                  <div class="d-flex">
                    <a href="javascript:;" class="text-secondary" @click="showModal('edit', (currentPage - 1) * itemsPerPage + index)">
                      <i class="fas fa-edit"></i>
                    </a>
                    <a href="javascript:;" class="text-danger ms-3" @click="confirmDelete((currentPage - 1) * itemsPerPage + index, role.id)">
                      <i class="fas fa-trash-alt"></i>
                    </a>
                  </div>
                </div>
                <div v-if="role.type == 1" class="mt-2">
                   <!-- CHANGED: 'min' to 'sec' -->
                   <p class="text-xxs mb-0"><strong>{{ $t('display_time') }}:</strong> {{ role.display_time }} sec</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Results Logic -->
      <div v-if="roles.length === 0" class="text-center py-4 text-secondary text-sm">
        No media found.
      </div>

      <!-- UPDATED: Added flex-wrap for mobile pagination screens -->
      <div class="pagination flex-wrap">
        <button @click="prevPage" :disabled="currentPage === 1">{{ $t('previous') }}</button>
        <button v-for="page in totalPages" :key="page" @click="goToPage(page)"
          :class="{ active: currentPage === page }">
          {{ page }}
        </button>
        <button @click="nextPage" :disabled="currentPage === totalPages">{{ $t('next') }}</button>
      </div>
    </div>

    <!-- Add Role Modal -->
    <div v-if="isAddModalVisible" class="modal fade show" style="display: block;" tabindex="-1" role="dialog"
      aria-labelledby="addRoleModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered px-2" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="addRoleModalLabel">{{ $t('add_media') }}</h5>
            <!-- Updated: Dynamic style for close button -->
            <button type="button" class="close" @click="hideModal('add')" aria-label="Close"
              :style="{ backgroundColor: 'var(--bs-primary)', color: 'white', border: '2px solid var(--bs-primary)', borderRadius: '50%' }">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body" style="max-height: 80vh; overflow-y: auto;">
            <form @submit.prevent="addRole">

              <!-- Upload File Section (shown first to select media) -->
              <div class="row mb-3">
                <div class="col-md-6 col-12">
                  <div class="form-group text-center">
                    <label for="profile_photo" class="form-control-label">{{ $t('upload_photo') }}</label>
                    <div v-if="isImageSelected">
                      <img :src="newRole.media_url || resourcePhotoUrl" alt="Profile Picture" class="img-fluid mb-2"
                        style="width: 100px; object-fit: cover; cursor: pointer;" @click="triggerProfileFileInput" />
                    </div>
                    <div v-if="isVideoSelected">
                      <video :src="newRole.media_url" class="img-fluid mb-2" style="width: 200px; cursor: pointer;"
                        controls />
                    </div>
                    <span v-if="errors.media_url" class="text-danger">{{ errors.media_url }}</span>
                    <input type="file" id="profile_photo" ref="profileFileInput" class="d-none"
                      aria-label="Profile Photo Upload" @change="onProfilePhotoChange" />
                  </div>
                </div>

                <div class="col-md-6 col-12">
                  <div class="form-group d-flex align-items-center justify-content-center">
                    <label class="mr-2">Inactive</label>
                    <label class="switch">
                      <input type="checkbox" v-model="newRole.status" :true-value="'1'" :false-value="'0'">
                      <span class="slider round"></span>
                    </label>
                    <label class="ml-2">Active</label>
                  </div>
                </div>
              </div>

              <!-- Media Type & Media Name (Hidden until video or image file is selected) -->
              <div v-if="newRole.media_url" class="row">
                <div class="form-group col-md-4 col-12">
                  <label for="roleName">{{ $t('media_type') }}</label>
                  <select class="form-control" id="roleName" v-model="newRole.type" required
                    @change="onChangeErrorClear">
                    <option value="0">Video</option>
                    <option value="1">Image</option>
                    <!-- <option value="2">Audio</option> -->
                  </select>
                </div>

                <div class="form-group col-md-8 col-12">
                  <label for="media_name">{{ $t('media_name') }}</label>
                  <input type="text" id="media_name" v-model="newRole.media_name" class="form-control"
                    placeholder="Enter media name" />
                  <span v-if="errors.media_name" class="text-danger">{{ errors.media_name }}</span>
                </div>
              </div>

              <!-- Display time only for images (Hidden until an image is selected) -->
              <div v-if="newRole.type == 1 && newRole.media_url" class="form-group">
                <label for="display_time">{{ $t('display_time') }}</label>
                <!-- CHANGED: 'minutes' to 'seconds' in placeholder -->
                <input type="number" id="display_time" v-model="newRole.display_time" class="form-control"
                  placeholder="Enter display time (seconds)" />
                <span v-if="errors.display_time" class="text-danger">{{ errors.display_time }}</span>
              </div>

              <button type="submit" class="btn btn-primary mt-3 w-100"
                :style="{ backgroundColor: 'var(--bs-primary)', color: 'white', border: '2px solid var(--bs-primary)' }">
                <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span v-else>{{ $t('add_media') }}</span>
              </button>

            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Role Modal -->
    <div v-if="isEditModalVisible" class="modal fade show" style="display: block;" tabindex="-1" role="dialog"
      aria-labelledby="editRoleModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered px-2" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editRoleModalLabel">{{ $t('update_role') }}</h5>
            <!-- Updated: Dynamic style for close button -->
            <button type="button" class="close" @click="hideModal('edit')" aria-label="Close"
              :style="{ backgroundColor: 'var(--bs-primary)', color: 'white', border: '2px solid var(--bs-primary)', borderRadius: '50%' }">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body" style="max-height: 80vh; overflow-y: auto;">
            <form @submit.prevent="updateRole">

              <div class="row">
                <div class="form-group col-md-4 col-12">
                  <label for="editRoleName">{{ $t('media_type') }}</label>
                  <select class="form-control" id="editRoleName" v-model="roles[selectedIndex].type" @change="onTypeChange" required>
                    <option value="0">Video</option>
                    <option value="1">Image</option>
                    <!-- <option value="2">Audio</option> -->
                  </select>
                </div>

                <div class="form-group col-md-8 col-12">
                  <label for="name">{{ $t('media_name') }}</label>
                  <input type="text" id="name" v-model="roles[selectedIndex].name" class="form-control"
                    placeholder="Enter media name" />
                    <small v-if="errors.name" class="text-danger">{{ errors.name }}</small>
                </div>

              </div>

              <div v-if="roles[selectedIndex].type == '1'" class="form-group">
                <label for="edit_display_time">{{ $t('display_time') }}</label>
                <!-- CHANGED: 'minutes' to 'seconds' in placeholder -->
                <input type="number" id="edit_display_time" v-model="roles[selectedIndex].display_time"
                  class="form-control" placeholder="Enter display time (seconds)" />
                  <small v-if="errors.display_time" class="text-danger">{{ errors.display_time }}</small>
              </div>

              <div class="row">
                <div class="col-md-6 col-12">
                  <div class="form-group text-center">
                    <label for="edit_profile_photo" class="form-control-label">{{ $t('upload_photo') }}</label>
                    <div>
                      <div v-if="roles[selectedIndex].type == 1">
                        <img :src="roles[selectedIndex].media_url || selectedRoles.media_url || resourcePhotoUrl" alt="Profile Picture"
                          class="img-fluid mb-2" style="width: 100px; object-fit: cover; cursor: pointer;"
                          @click="triggerEditProfileFileInput" />

                      </div>
                      <div v-if="roles[selectedIndex].type == 0">
                        <video :src="roles[selectedIndex].media_url || selectedRoles.media_url" class="img-fluid mb-2"
                          style="width: 200px; cursor: pointer;" controls />
                      </div>
                      <small v-if="errors.media_url" class="text-danger">{{ errors.media_url }}</small>
                    </div>
                    <input type="file" id="edit_profile_photo" ref="editProfileFileInput" class="d-none"
                      aria-label="Profile Photo Upload" @change="onEditProfilePhotoChange" />
                  </div>
                </div>

                <div class="col-md-6 col-12">
                  <div class="form-group d-flex align-items-center justify-content-center">
                    <label class="mr-2">Inactive</label>
                    <label class="switch">
                      <input type="checkbox" v-model="roles[selectedIndex].status" :true-value="'1'" :false-value="'0'">
                      <span class="slider round"></span>
                    </label>
                    <label class="ml-2">Active</label>
                  </div>
                </div>
              </div>

              <button type="submit" class="btn btn-primary w-100"
                :style="{ backgroundColor: 'var(--bs-primary)', color: 'white', border: '2px solid var(--bs-primary)' }">
                <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span v-else>{{ $t('update_media') }}</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>


  </div>
</template>
<script>
import resourcePhotoUrl from "@/assets/img/resource.png";
import SoftBadge from "@/components/SoftBadge.vue";
import '@fortawesome/fontawesome-free/css/all.css';
import MediaDataService from "../../services/MediaDataService.js";
import Swal from 'sweetalert2';

export default {
  name: "MediaTable",
  components: {
    SoftBadge,
  },
  data() {
    return {
      viewMode: 'card', 
      isLoading: false, 
      roles: [],
      newRole: {
        name: "",
        type: "1",
        media_url: "",
        display_time: "",
        status: 'Inactive'
      },
      resourcePhotoUrl,
      selectedIndex: null,
      isAddModalVisible: false,
      isEditModalVisible: false,
      selectedRoles: [], // For selecting roles in add mode
      currentPage: 1,
      itemsPerPage: 10,
      selectedLanguage: this.$i18n.locale,
      isImageSelected: true,
      isVideoSelected: false,
      errors: {},
      tenantId: localStorage.getItem('tenant_id') || localStorage.getItem('tenantId') || null,
      installationTypeId: localStorage.getItem('installation_type_id') || null
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.roles.length / this.itemsPerPage);
    },
    paginatedRoles() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.roles.slice(start, end);
    },
  },
  created() {
    this.getAllRoles();
    const socket = this.$socket || (this.$root && this.$root.socket) || (window.socket);
    if (socket) {
      console.log("📡 [VUE SOCKET ATTACHED] Listening for MEDIA_LIBRARY_UPDATED...");
      socket.on('MEDIA_LIBRARY_UPDATED', (data) => {
        console.log('⚡ [REAL-TIME WEB REFRESH] New media detected from mobile/backend:', data);
        this.getAllRoles(); // 👈 Automatically fetches new media and updates the screen!
      });
    }
  },
  beforeUnmount() {
    // Clean up socket listener when moving to another page
    const socket = this.$socket || (this.$root && this.$root.socket) || (window.socket);
    if (socket) {
      socket.off('MEDIA_LIBRARY_UPDATED');
    }
  },
  methods: {
    onTypeChange() {
      this.roles[this.selectedIndex].name = ""; 
      this.roles[this.selectedIndex].media_url = ""; 
      this.roles[this.selectedIndex].mediaFile = ""; 
  },
    switchLanguage(lang) {
      this.$i18n.locale = lang;
    },
    validateFields() {
      this.errors = {}; // Reset errors

      if (!this.newRole.media_name) {
        this.errors.media_name = "Media name is required.";
      }

      if (!this.newRole.media_url) {
        this.errors.media_url = "Please select a file to upload.";
      }

      if (this.newRole.type == "1" && (!this.newRole.display_time || this.newRole.display_time <= 0)) {
        this.errors.display_time = "Display time is required and must be greater than 0.";
      }

      return Object.keys(this.errors).length === 0; // Returns true if no errors
    },
    getAllRoles() {
      this.isLoading = true;

      // Ensure tenant & installation context parameters are resolved for strict tenant DB isolation
      const tenantId = this.tenantId || localStorage.getItem('tenant_id') || localStorage.getItem('tenantId') || null;
      const installationTypeId = this.installationTypeId || localStorage.getItem('installation_type_id') || null;
      const params = {};
      if (tenantId) params.tenant_id = tenantId;
      if (installationTypeId) params.installation_type_id = installationTypeId;

      MediaDataService.getAll(params)
        .then(response => {
          this.roles = Array.isArray(response.data) ? response.data : (response.data?.data || []);
          console.log(this.roles);
          this.isLoading = false;
        })
        .catch(e => {
          this.isLoading = false;
          console.log(e);
        });
    },
    addRole() {
      if (!this.validateFields()) {
        this.isLoading = false;
        return; // Stop execution if validation fails
      }
      const formData = new FormData();
      formData.append("file", this.newRole.mediaUrl);
      formData.append("name", this.newRole.media_name);
      formData.append("type", this.newRole.type);
      formData.append("status", this.newRole.status);
      formData.append("display_time", this.newRole.display_time);

      // Append tenant context if resolved to ensure records are isolated to tenant database
      const tenantId = this.tenantId || localStorage.getItem('tenant_id') || localStorage.getItem('tenantId');
      if (tenantId) {
        formData.append("tenant_id", tenantId);
      }

      try {
        MediaDataService.create(formData)
          .then(response => {
            console.log(response.data.media);

            if (response.data.media) {
              Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Media added successfully!',
                confirmButtonText: 'OK'
              }).then(() => {
                this.getAllRoles();
              });
            }

          })
          .catch(e => {
            this.isLoading = false;
            console.log(e);
          });

      } catch (error) {
        console.error("Upload failed:", error);
        alert("Error uploading file.");
      }
      this.resetForm('add');
    },
    confirmDelete(index, id) {
      Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteMedia(index, id);
          Swal.fire(
            'Deleted!',
            'The Media has been deleted.',
            'success'
          );
        }
      });
    },
    deleteMedia(index, id) {
      MediaDataService.delete(id)
        .then(response => {
          console.log(response);
          this.roles.splice(index, 1);
        })
        .catch(e => {
          console.log(e);
        });
    },
    showModal(mode, index = null) {
      if (mode === 'add') {
        this.isAddModalVisible = true;
        this.selectedRoles = [];
        this.newRole = { name: "", media_name: "", media_url: "", type: "", status: "1" };
      } else if (mode === 'edit' && index !== null) {
        this.selectedIndex = index;
        this.isEditModalVisible = true;
      }
      this.isLoading = false;
    },
    triggerProfileFileInput() {
      this.$refs.profileFileInput.click();
    },
    triggerEditProfileFileInput() {
      this.$refs.editProfileFileInput.click();
    },
    onChangeErrorClear() {
      this.errors = {};
    },
// Inside MediaTable.vue -> methods:

onProfilePhotoChange(event) {
  const file = event.target.files[0];
  if (file) {
    const reader = new FileReader();
    const fileType = file.type.split("/")[0];

    reader.onload = e => {
      this.newRole.media_url = e.target.result;
      this.newRole.mediaUrl = file;

      // Automatically set media_name from file name
      if (!this.newRole.media_name) {
        this.newRole.media_name = file.name.replace(/\.[^/.]+$/, "");
      }

      if (fileType === "image") {
        this.isImageSelected = true;
        this.isVideoSelected = false;
        this.newRole.type = "1"; // Automatically set type to Image
      } else if (fileType === "video") {
        this.isImageSelected = false;
        this.isVideoSelected = true;
        this.newRole.type = "0"; // Automatically set type to Video

        // --- ADD THIS LOGIC TO GET VIDEO DURATION ---
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
          window.URL.revokeObjectURL(video.src);
          // Duration is in seconds. 
          // If you want minutes (like 2.17), use: (video.duration / 60).toFixed(2)
          // Since your DB is INTEGER, let's store total seconds or round it.
          this.newRole.display_time = Math.round(video.duration); 
        };
        video.src = URL.createObjectURL(file);
        // --------------------------------------------
      }
    };
    reader.readAsDataURL(file);
  }
},

onEditProfilePhotoChange(event) {
  const file = event.target.files[0];
  if (file) {
    const reader = new FileReader();
    const fileType = file.type.split("/")[0];

    reader.onload = (e) => {
      this.roles[this.selectedIndex].media_url = e.target.result;
      this.roles[this.selectedIndex].mediaFile = file;

      if (fileType === "image") {
        this.roles[this.selectedIndex].type = "1";
      } else if (fileType === "video") {
        this.roles[this.selectedIndex].type = "0";
        // --- ADD THIS LOGIC FOR EDIT MODE ---
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
          window.URL.revokeObjectURL(video.src);
          this.roles[this.selectedIndex].display_time = Math.round(video.duration);
        };
        video.src = URL.createObjectURL(file);
        // ------------------------------------
      }
    };
    reader.readAsDataURL(file);
  }
},
    hideModal(mode) {
      if (mode === 'add') {
        this.isAddModalVisible = false;
      } else if (mode === 'edit') {
        this.isEditModalVisible = false;
      }
      this.selectedIndex = null;
      this.isLoading = false;
      this.errors = {};
    },
    updateRole() {
      this.errors = {};
      this.isLoading = true;
      const updatedRole = { ...this.roles[this.selectedIndex] };
     
      if (!updatedRole.name) {
        this.errors.name = "Media name is required.";
      }
     
      if (updatedRole.type == "1" && !updatedRole.display_time) {
        this.errors.display_time = "Display time is required for images.";
      }
      if (!updatedRole.media_url && !updatedRole.mediaFile) {
        this.errors.media_url = "Please upload a media file.";
      }

      if (Object.keys(this.errors).length > 0) {
        this.isLoading = false;
        return;
      }

      const formData = new FormData();

      if (updatedRole.mediaFile) {
        formData.append("file", updatedRole.mediaFile); 
      } else {
        formData.append("file", updatedRole.media_url); 
      }

      formData.append("name", updatedRole.name);
      formData.append("type", updatedRole.type);
      formData.append("status", updatedRole.status);
      formData.append("display_time", updatedRole.display_time || 0);

// src/views/components/Media.vue inside updateRole() method

MediaDataService.update(this.roles[this.selectedIndex].id, formData)
  .then((response) => {
    if (response.data.status) {
      this.isEditModalVisible = false;
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Media updated successfully!',
      }).then(() => {
        // THIS IS THE KEY: Re-fetching the list removes the inactive item
        this.getAllRoles(); 
      });
    }
  })
        .catch((e) => {
          this.isLoading = false;  
          console.log(e);
        });
    },
    resetForm(mode) {
      if (mode === 'add') {
        this.newRole = { name: "", status: "Active" };
        this.selectedRoles = [];
        this.isAddModalVisible = false;
      } else if (mode === 'edit') {
        this.selectedIndex = null;
        this.isEditModalVisible = false;
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
  },
};
</script>

<style scoped>
/* Focus Fix: Uses dynamic variable for borders */
.form-control:focus, select:focus, input:focus {
  outline: none !important;
  box-shadow: 0 0 0 3px rgba(52, 211, 153, 0.15) !important;
  border-color: var(--bs-primary) !important;
}

/* Primary Color Overrides using variables */
.btn-primary {
  background-color: var(--bs-primary) !important;
  border-color: var(--bs-primary) !important;
}

.btn-outline-primary {
  border-color: var(--bs-primary) !important;
  color: var(--bs-primary) !important;
}

.btn-outline-primary:hover, .btn-primary:active {
  background-color: var(--bs-primary) !important;
  color: white !important;
}

.langdrop {
  width: 130px;
  margin-left: 87%;
}

@media (min-width:300px) and (max-width:550px) {
  .langdrop {
    width: 130px;
    margin-left: 57%;
  }
}

.switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

/* Updated: Using dynamic theme color for the switch */
input:checked+.slider {
  background-color: var(--bs-primary) !important;
}

input:checked+.slider:before {
  transform: translateX(26px);
}

.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}

.table th,
.table td {
  text-align: left;
  padding: 12px;
}

.modal.fade.show {
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-dialog {
  margin-top: 10%;
}

.fa-edit,
.fa-trash-alt {
  font-size: 1rem;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;
}

/* Updated: Pagination uses dynamic theme color */
.pagination button {
  background-color: var(--bs-primary);
  color: white;
  border: 2px solid var(--bs-primary);
  padding: 0.5rem 1rem;
  border-radius: 10px;
  margin: 0.5rem; /* Updated for wrapping */
  cursor: pointer;
}

.pagination button:disabled {
  background-color: #ffffff;
  color: #000000;
  cursor: not-allowed;
  border: 2px solid #ddd;
}

.pagination span {
  margin: 0 0.5rem;
}

.pagination button.active {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
  border-radius: 50px;
  font-weight: bold;
}

.text-danger {
  font-size: 14px;
}

.object-fit-cover {
  object-fit: cover;
}
.uppercase {
  text-transform: uppercase;
}

/* Updated: Badge using dynamic color variable */
:deep(.badge.bg-gradient-success) {
  background-image: linear-gradient(310deg,  var(--bs-primary) 0%,  var(--bs-primary) 100%) !important;
  background-color:  var(--bs-primary) !important;
}

:deep(.bg-success) {
  background-color:  var(--bs-primary) !important;
}

/* --- ADDED MOBILE RESPONSIVE CSS --- */
@media (max-width: 768px) {
  .card-header {
    padding-bottom: 15px !important;
  }
  .modal-dialog {
    margin: 0.5rem;
    margin-top: 20%;
  }
  .pagination button {
    padding: 0.4rem 0.8rem;
    font-size: 12px;
    margin: 2px;
  }
}
</style>